import React, { useMemo } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Divider from '@amzn/meridian/divider';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import _ from 'lodash';
import { type ErrorRecord, type HandleChange } from 'src/hooks/useForm';
import {
  type DownloadTWPAForm,
  type PlacementDetailsAllocatedJobShift,
} from 'src/store/placementDetailsPageSlice/types';
import { allocatedJobShiftsToModificationDisplayList } from 'src/utils/placementDetailUtils';

// **** Types **** //

interface GeneralInfoProps {
  handleChange: HandleChange<DownloadTWPAForm>;
  tierTwoExperienceEnabled: boolean;
  jobModificationsEnabled: boolean;
  twpaFormData: Partial<DownloadTWPAForm>;
  twpaFormErrors: ErrorRecord<DownloadTWPAForm>;
  allocatedJobShifts: PlacementDetailsAllocatedJobShift[];
}

// **** Components **** //

/**
 * Default component
 */
const GeneralInfo = (props: GeneralInfoProps) => {
  const {
    handleChange,
    jobModificationsEnabled,
    tierTwoExperienceEnabled,
    twpaFormData,
    twpaFormErrors,
    allocatedJobShifts = [],
  } = props;

  const getModificationList = () => {
    if (tierTwoExperienceEnabled) {
      return <T2ModificationsList allocatedJobShifts={allocatedJobShifts} />;
    }

    if (jobModificationsEnabled) {
      return <ModificationsList allocatedJobShifts={allocatedJobShifts} />;
    }

    return (
      <LegacyJobModificationsField
        value={twpaFormData.jobModifications}
        onChange={(jobModifications) => handleChange({ jobModifications })}
        error={!!twpaFormErrors.jobModifications?.error}
        errorMessage={twpaFormErrors.jobModifications?.errorMessage}
      />
    );
  };

  return (
    <div>
      <Text>
        Fill out the following information. This will be used to create a PDF version of the Associate's Temporary Work
        Placement Acknowledgement letter.
      </Text>
      <Row widths={['fill', 'fill']} spacingInset="400 none none">
        <Column spacing="200" spacingInset="200">
          <Text tag="label" type="b300" color="primary">
            Department
          </Text>
          <Input
            aria-label="Department"
            placeholder="Associate's assigned department"
            value={twpaFormData.department}
            onChange={(department) => handleChange({ department })}
            error={twpaFormErrors.department?.error}
            errorMessage={twpaFormErrors.department?.errorMessage}
          />
        </Column>
        <Column spacing="200" spacingInset="200">
          <Text tag="label" type="b300" color="primary">
            Placement Shift(s)
          </Text>
          <Input
            aria-label="Placement Shifts"
            placeholder="Associate's placeholder shift"
            value={twpaFormData.placementShifts}
            onChange={(placementShifts) => handleChange({ placementShifts })}
            error={twpaFormErrors.placementShifts?.error}
            errorMessage={twpaFormErrors.placementShifts?.errorMessage}
          />
        </Column>
      </Row>
      <Row widths={['fill']}>
        <Column spacing="200" spacingInset="200">
          <Text tag="label" type="b300" color="primary">
            Additional Notes
          </Text>
          <Input
            aria-label="Notes"
            placeholder="Enter addtional notes"
            value={twpaFormData.notes}
            onChange={(notes) => handleChange({ notes })}
            error={twpaFormErrors.notes?.error}
            errorMessage={twpaFormErrors.notes?.errorMessage}
          />
        </Column>
      </Row>
      <Row widths={['fill']}>
        <Column spacing="200" spacingInset="200">
          {getModificationList()}
        </Column>
      </Row>
      <Row widths={['50%']}>
        <Column spacing="200" spacingInset="200">
          <Text tag="label" type="b300" color="primary">
            Report Date
          </Text>
          <DatePicker
            aria-label="Report Date"
            value={twpaFormData.reportDate}
            onChange={(reportDate) => handleChange({ reportDate })}
            placeholder="MM/DD/YYYY"
            error={twpaFormErrors.reportDate?.error}
            errorMessage={twpaFormErrors.reportDate?.errorMessage}
          />
        </Column>
      </Row>
      <Divider spacingBefore="400" spacingAfter="400" />
    </div>
  );
};

/**
 * Display Modifications as a bullet-list.
 */
const ModificationsList = (props: { allocatedJobShifts: PlacementDetailsAllocatedJobShift[] }) => {
  const { allocatedJobShifts } = props;

  // Get a list of human readable modifications from the allocatedJobShifts list
  const modificationsList = useMemo(() => {
    return allocatedJobShiftsToModificationDisplayList(allocatedJobShifts);
  }, [allocatedJobShifts]);

  return (
    <>
      <Text tag="label" type="b300" color="primary">
        Modifications
      </Text>
      {!modificationsList.length ? (
        <Text color="secondary">N/A</Text>
      ) : (
        <Text tag="ul">
          {modificationsList.map((item, i) => (
            <li key={i}>
              <Box>{item.modificationText}</Box>
              {item.taskNames.map((taskName, i) => (
                <Box key={i}>
                  <Text type="b200">Restriction: {taskName}</Text>
                </Box>
              ))}
            </li>
          ))}
        </Text>
      )}
    </>
  );
};

/**
 * Display Modifications as a bullet-list.
 */
const T2ModificationsList = (props: { allocatedJobShifts: PlacementDetailsAllocatedJobShift[] }) => {
  const { allocatedJobShifts } = props;

  return (
    <>
      <Text tag="label" type="b300" color="primary">
        Modifications
      </Text>

      <Text tag="ul">
        {allocatedJobShifts.map((jobShift, i) => (
          <Text tag="li" key={i}>
            {jobShift.jobName}: {jobShift.modificationNotes}
          </Text>
        ))}
      </Text>
    </>
  );
};

/**
 * Display the legacy component for JobModifications (Input bar)
 */
const LegacyJobModificationsField = (props: {
  value: string | undefined;
  onChange: (value: string) => void;
  error: boolean;
  errorMessage: string | undefined;
}) => {
  const { value, onChange, error, errorMessage } = props;
  return (
    <>
      <Text tag="label" type="b300" color="primary">
        Job Modifications
      </Text>
      <Input
        aria-label="Job Modifications"
        placeholder="Enter job modifications"
        value={value}
        onChange={onChange}
        error={error}
        errorMessage={errorMessage}
      />
    </>
  );
};

// **** Export **** //

export default GeneralInfo;
